import React from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import * as Sentry from "@sentry/react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
    this.setState({ errorInfo });
    
    // Reportar a Sentry explícitamente
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          height: '100vh', 
          width: '100vw', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          background: 'var(--bg-dark)',
          color: '#fff',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{ 
            padding: '3rem', 
            borderRadius: '2rem', 
            background: 'rgba(255,255,255,0.03)', 
            border: '1px solid rgba(255,255,255,0.08)',
            maxWidth: '500px',
            backdropFilter: 'blur(20px)'
          }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '24px', 
              background: 'rgba(255, 50, 50, 0.1)', 
              display: 'grid', 
              placeItems: 'center', 
              margin: '0 auto 1.5rem',
              border: '1px solid rgba(255, 50, 50, 0.2)'
            }}>
              <AlertTriangle size={40} color="#ff3232" />
            </div>
            <h1 style={{ fontSize: '2rem', fontWeight: 900, marginBottom: '1rem' }}>¡Lo sentimos!</h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: '2rem', lineHeight: 1.6 }}>
              Algo no salió como esperábamos. No te preocupes, ya estamos trabajando para arreglarlo.
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={() => window.location.reload()} 
                className="btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}
              >
                <RefreshCcw size={18} /> Reintentar
              </button>
              <button 
                onClick={() => window.location.href = '/'} 
                className="btn-outline"
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}
              >
                <Home size={18} /> Ir al Inicio
              </button>
            </div>
            
            <div style={{ marginTop: '1.5rem' }}>
              <button
                onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.4)',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.7rem'
                }}
              >
                {this.state.showDetails ? 'Ocultar' : 'Detalles del error'}
              </button>
              {this.state.showDetails && (
                <div style={{
                  marginTop: '0.8rem',
                  padding: '0.8rem',
                  background: 'rgba(255,0,0,0.08)',
                  borderRadius: '0.5rem',
                  fontSize: '0.65rem',
                  color: 'rgba(255,255,255,0.6)',
                  textAlign: 'left',
                  maxHeight: '200px',
                  overflow: 'auto',
                  wordBreak: 'break-all'
                }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.3rem' }}>
                    {this.state.error && this.state.error.toString()}
                  </p>
                  <pre style={{ fontSize: '0.6rem', whiteSpace: 'pre-wrap', margin: 0 }}>
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
